/* Every prompt the AI action flows send, plus the small builders that
   assemble them. Kept free of component state on purpose: each builder takes
   what it needs as arguments, so prompts can be read (and changed) without
   reading the panels. */
import { ImageCategory, type ScrapedImage } from "../../types"
import type { RefRole } from "./types"

// How each role is described to the model in the numbered image manifest.
// The manifest is generated from the SAME ordered list that is sent as
// `referenceAssetIds`, so "Image N" always points at the right asset.
export const ROLE_PROMPT_LINES: Record<RefRole, string> = {
  product:
    "another photo of the SAME product from a different angle — use it only to render the product accurately",
  packshot:
    "a clean PACKSHOT of the SAME product on a plain background — the most accurate reference for its true shape, colour and detailing",
  lifestyle:
    "a LIFESTYLE photo of the SAME product in a real setting — use it for scene, environment and mood",
  onModel:
    "an ON-MODEL photo of the SAME product being worn — use it for fit, drape and scale on a person",
  detail:
    "a CLOSE-UP DETAIL of the SAME product — use it for material, texture and construction accuracy",
  background:
    "a BACKGROUND reference — take the scene, setting and background from this image, NOT the product",
  style:
    "a STYLE reference — match its mood, lighting and color grading, do not copy its objects",
}

// Feed-sourced assets carry a real classification (the product-feed crawler
// sorted every image into laydown / lifestyle / on-model / detail). Telling the
// model exactly WHAT each reference depicts beats the generic "supporting
// imagery" line — it can then use a packshot for product accuracy and a
// lifestyle frame for scene and mood, instead of guessing from pixels.
export const CATEGORY_PROMPT_LINES: Partial<Record<ImageCategory, string>> = {
  [ImageCategory.LAYDOWN]:
    "a clean PACKSHOT of the SAME product on a plain background — the most accurate reference for the product's true shape, colour and detailing",
  [ImageCategory.LIFESTYLE]:
    "a LIFESTYLE photo of the SAME product in a real setting — use it for scene, environment and mood",
  [ImageCategory.ON_BODY]:
    "an ON-MODEL photo of the SAME product being worn — use it for fit, drape and scale on a person",
  [ImageCategory.PRODUCT_DETAIL]:
    "a CLOSE-UP DETAIL of the SAME product — use it for material, texture and construction accuracy",
  [ImageCategory.FABRIC_DETAIL]:
    "a FABRIC close-up of the SAME product — use it for material and texture accuracy",
}

/**
 * The manifest line for a reference image. A known feed category wins over the
 * generic role line, since it says what the picture actually contains.
 */
export const describeRef = (a: ScrapedImage, fallback: string) =>
  CATEGORY_PROMPT_LINES[a.category] ?? fallback

/** The numbered image manifest. Built from the SAME ordered list that is sent
 *  as reference ids, so "Image N" always points at the right asset. */
export const buildImageManifest = (
  inputs: { assetId: string; roleLine: string }[],
) =>
  `You are given ${inputs.length} reference image${
    inputs.length === 1 ? "" : "s"
  }, numbered in the exact order provided. ${inputs
    .map((inp, i) => `Image ${i + 1}: ${inp.roleLine}.`)
    .join(" ")}`

/** The manifest sent with a motion prompt. Veo gets the hero as reference #1
 *  followed by the extra shots, so the numbering matches what it receives. */
export const buildMotionManifest = (
  inputs: { assetId: string; roleLine: string }[],
) =>
  inputs.length < 2
    ? ""
    : `You are given ${inputs.length} reference images for this video, numbered in the exact order provided. ${inputs
        .map((inp, i) => `Image ${i + 1}: ${inp.roleLine}.`)
        .join(" ")}`

/** Manifest line for the hero shot — always image 1 in every flow. */
export const HERO_ROLE_LINE =
  "the HERO PRODUCT shot — this is the exact product being advertised"

/** Manifest line for pool images the user never explicitly assigned a role
 *  to (the "I'm feeling lucky" widening). */
export const SUPPORTING_ROLE_LINE =
  "supporting product/brand imagery — decide how best to use it: extra product angle, background, or mood reference"

// The creative-director base prompt — the DEFAULT for every compose pass
// (the "I'm feeling lucky" behavior). The user's brief items (background,
// headline, CTA) are appended on top as hard requirements that override
// the freedoms granted here.
export const CREATIVE_DIRECTOR_PROMPT = `You are an expert advertising creative director. Using the numbered reference images described above, design ONE scroll-stopping advertising photo of the hero product. Respect each image's stated role: the product itself must come ONLY from the hero product shot and any product-angle images; images marked as background, style, or supporting imagery inform the scene and mood, never the product. You decide the final composition, background, scene, lighting and mood that sell this product best — a clean studio look, a lifestyle scene, or a bold color backdrop — unless a requirement below dictates otherwise. Keep the product EXACTLY as it is: same shape, proportions, colors, materials, labels and any text or logos on it. Unless told otherwise below, you may add ONE short, punchy, perfectly spelled headline if it strengthens the ad — nothing else. No watermarks, no borders, no invented logos. The result must look like a premium, professional ad.`

// Derives the non-master ratios FROM the master frame so every placement
// looks like the same ad. Mirrors the enhance/outpaint discipline: extend
// the scene, never redesign it.
export const REFRAME_PROMPT = `Recreate THIS EXACT advertising image at a different aspect ratio. Keep everything identical — the product (exact shape, colors, materials, labels and any text on it), the headline and all other text character-for-character in the same style, the background scene, lighting, color grading and overall composition. To fill the new canvas, naturally extend the existing scene outward (continue the same background) or gently reframe — never distort, restyle, move key elements out of view, or introduce anything new. The result must read as the SAME ad, just recomposed for the new format.`

export const LUCKY_MOTION_PROMPT = `Direct a premium product commercial from this ad frame: cinematic camera movement, polished lighting with soft moving highlights and subtle parallax depth. Keep the product exactly as it is, and keep any on-image text static, crisp and perfectly legible throughout. Make it feel like a high-end social media ad.`

// ── Carousel prompts ─────────────────────────────────────────────────────

// Appended to the full brief prompt when composing card 1 — the card that
// establishes the campaign's art direction for the whole set.
export const carouselFirstCardSuffix = (count: number) =>
  `This image is CARD 1 of a ${count}-card carousel ad — the scroll-stopping opener the user swipes from. Design it as the hook: hero product front and center, bold and instantly readable at feed size.`

// Cards 2..N are derived FROM card 1 (plus the hero shot for product
// accuracy) so the whole set reads as one campaign. Middle cards vary the
// angle/benefit; the last card closes with the call to action.
export const buildCarouselCardPrompt = (
  cardNumber: number,
  count: number,
  cta: string,
) => {
  const isLast = cardNumber === count
  const parts: string[] = [
    `You are given 2 reference images. Image 1: CARD 1 of this carousel ad — the campaign's established art direction; match its background style, color palette, lighting, typography and overall mood EXACTLY. Image 2: the hero product shot — the product must come ONLY from this image, kept exactly as it is: same shape, proportions, colors, materials, labels and any text or logos on it.`,
    `You are an expert advertising creative director designing CARD ${cardNumber} of a ${count}-card carousel ad. The card must unmistakably read as the SAME campaign as Image 1 while showing something NEW —`,
    isLast
      ? `this is the CLOSING card: drive action. Feature the product one final time with a clear sense of resolution.`
      : `show the product from a different angle, in a different usage context, or highlighting a different benefit than the previous cards.`,
  ]
  if (isLast && cta.trim()) {
    parts.push(
      `Call-to-action requirement: add "${cta.trim()}" in a simple, clean button-like badge.`,
    )
  }
  parts.push(
    isLast && cta.trim()
      ? "Do NOT invent any other text — the only text allowed is the requested call-to-action and whatever is already on the product."
      : "You may add ONE short, punchy, perfectly spelled supporting line if it strengthens the card — do NOT repeat card 1's headline.",
    "No watermarks, no borders, no invented logos. The result must look like a premium, professional ad.",
  )
  return parts.join(" ")
}

// Per-card refine: takes the card's CURRENT image as the base and applies
// only the user's instruction — edits stack, so a card can be iterated on
// repeatedly without touching its siblings. An optional third image serves
// as the reference for the requested change (new background, style, extra
// product angle).
export const buildCardRefinePrompt = (
  instruction: string,
  hasRefImage: boolean,
) =>
  [
    `You are given ${hasRefImage ? 3 : 2} reference images. Image 1: an advertising image — the base you are editing. Image 2: the hero product shot — the product must stay EXACTLY as shown here.`,
    hasRefImage
      ? "Image 3: the REFERENCE for the requested change — take the relevant scene, style, or element from it as the change describes, never the product."
      : "",
    `Recreate Image 1 applying ONLY this change: ${instruction.trim()}. Keep everything else identical — the product (exact shape, proportions, colors, materials, labels and any text on it), all other text character-for-character in the same style, the background scene, lighting, color grading and composition. No watermarks, no borders, no invented logos. The result must read as the SAME ad card with just that change applied.`,
  ]
    .filter(Boolean)
    .join(" ")

// ── Enhance preset ───────────────────────────────────────────────────────

// Preset prompt for the upscale/enhance flow (formerly its own
// AiEnhanceDialog). Re-exported by AiActionDialog so the AdForgePage low-res
// banner can run the same enhancement inline without opening the dialog.
//
// This runs through the Imagen EDIT_MODE_OUTPAINT path, so the enhance does
// TWO jobs in one pass: (1) upscale/restore the existing pixels and (2) fill
// any larger frame by continuing the scene outward. The prompt has to enable
// the extension while hard-blocking invented content — otherwise the model
// "adds random stuff" in the new area instead of seamlessly continuing what's
// already at the edges.
export const ENHANCE_PROMPT = `Do TWO things at once to THIS EXACT photograph, and nothing else. (1) UPSCALE & RESTORE: output a sharp, high-resolution (1080px+) version of the same image — recover genuine fine detail and texture and remove blur, softness, sensor noise, JPEG/compression artifacts, banding, halos, and pixelation. (2) EXTEND TO FILL THE FRAME: wherever the canvas is larger than the original, seamlessly continue the EXISTING scene outward to fill it — extend only what is already present at the edges (the same wall, floor, ground, sky, surface, fabric, or background), as if the same camera captured a slightly wider view of the same moment. Keep the original subject, pose, faces and identity, hands, clothing, products, props, and any existing text, numbers, or logos exactly as they are and in their exact positions — character-for-character and shape-for-shape. Match the original lighting, shadows, reflections, color palette, white balance, focal length, depth of field, grain, and photographic style so the upscaled detail and any extended area are a flawless, invisible continuation. Do NOT invent or add new people, objects, products, props, patterns, scenery, text, or logos; do NOT restyle, beautify, reposition, or reinterpret anything. No borders, frames, letterboxing, solid color bars, padding, gradients, or blur — every pixel must be continuous, realistic, sharp scene content. The result is the same photo, just higher-resolution and naturally filling the frame.`
